'use client'

import { ProductReviewsFilter } from '@/app/[locale]/product/[slug]/_components/product-reviews/filter'
import { ProductReviewsItem } from '@/app/[locale]/product/[slug]/_components/product-reviews/product-reviews-item'
import { ProductReviewsRating } from '@/app/[locale]/product/[slug]/_components/product-reviews/rating'
import { Pagination } from '@/components/ui/pagination'
import { useScrollToHashSection } from '@/hooks/common/use-scroll-to-hash-section'
import { useReview } from '@/hooks/react-query/reviews/use-review'
import { PaginatedReviewResponse } from '@/types/api'
import { ReviewItem, ReviewResponse } from '@/types/collection'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'
import { ProductReviewsEmpty } from './reviews-empty'
import { ProductReviewsSizeFitDistribution } from './size-fit-distribution'

type Props = {
  productId: string
  reviewAvg: number
  reviewCount: number
}

export const ProductReviews = ({
  productId,
  reviewAvg,
  reviewCount,
}: Props) => {
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState<string>('')
  const [keywords, setKeywords] = useState<string>('')
  const t = useTranslations('product_detail.title')
  const sectionRef = useRef<HTMLElement>(null)

  const { data, isLoading } = useReview(productId, {
    page,
    limit: 5,
    filter,
    keywords,
  })

  useScrollToHashSection(sectionRef, '#product-reviews', !isLoading)

  // Cast to correct type
  const reviewData = data as PaginatedReviewResponse<ReviewResponse> | undefined
  const reviews = reviewData?.data?.list || []
  const totalPages = reviewData?.last_page || 1

  const handleFilterChange = (value: string) => {
    setFilter(value)
    setPage(1) // Reset to first page when filter changes
  }

  const handleSearchChange = (value: string) => {
    setKeywords(value)
    setPage(1) // Reset to first page when search changes
  }

  return (
    <section
      ref={sectionRef}
      className='bg-neutral-100 px-4 py-10 md:pt-20 lg:hidden'
      id='product-reviews'
      aria-label={t('product_reviews')}
    >
      <h2 className='mb-5.5 font-criteria text-lg font-medium uppercase leading-6.5 text-neutral-900'>
        {t('product_reviews')}
      </h2>
      <ProductReviewsRating reviewAvg={reviewAvg} reviewCount={reviewCount} />

      <ProductReviewsSizeFitDistribution
        bodyFit={reviewData?.data?.body_fit || []}
      />

      <ProductReviewsFilter
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        disabled={!reviews?.length}
      />
      <div
        className='mt-5 space-y-3'
        role='list'
        aria-label='Product reviews list'
      >
        {reviews.length > 0 ? (
          reviews.map((review: ReviewItem) => (
            <ProductReviewsItem key={review._id} review={review} />
          ))
        ) : reviewCount === 0 ? (
          <ProductReviewsEmpty className='py-4' />
        ) : (
          <div
            className='flex h-full items-center justify-center py-4'
            role='alert'
            aria-live='polite'
          >
            <p className='text-center text-sm text-neutral-500'>
              {t('no_reviews')}
            </p>
          </div>
        )}
      </div>
      <Pagination
        totalPages={totalPages}
        page={page}
        onPageChange={setPage}
        wrapperClassName='mt-3'
      />
    </section>
  )
}
